import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Criteria, CriteriaVehicleModels } from '@wb/core/interfaces/criteria';
import { Subject } from 'rxjs';

export interface CriteriaItem {
  type: 'label' | 'value';
  value?: string;
}

export enum CriteraTypes {
  ACCESSORIES = 'ACCESSORIES',
  EXCLUSIONS = 'EXCLUSIONS',
  FEES_TAXES = 'FEES_TAXES',
  FI = 'FI',
  PROTECTION = 'PROTECTION',
}

@Injectable({
  providedIn: 'root',
})
export class CriteriaService {
  public criteriaAdded$: Subject<boolean> = new Subject<boolean>();

  constructor(private readonly translate: TranslateService) {}

  // Generate readable interpretation of a criteria in the form of an ordered array of criteriaItems
  generateCriteriaItems(criteria: Criteria): CriteriaItem[] {
    const result: CriteriaItem[] = [];
    const criteriaPrefix = 'addCriteria.criteria.';

    const createLabel = (value: string): CriteriaItem => {
      const translatedValue = this.translate.instant(`addCriteria.labels.${value}`);
      return { type: 'label', value: translatedValue };
    };

    const createValue = (value?: string): CriteriaItem => {
      return { type: 'value', value };
    };

    // Disply if IS or IS NOT a NEW and/or USED deal
    result.push(createLabel('if'));
    result.push(createValue(this.translate.instant(`${criteriaPrefix}operator.${criteria.operator}`)));
    result.push(createLabel('a'));
    result.push(createValue(this.translate.instant(`${criteriaPrefix}vehicleCondition.${criteria.vehicle_condition}`)));
    result.push(createLabel('and'));

    // Display dealType
    const dealTypes = criteria.deal_type?.split(',');
    if (dealTypes) {
      result.push(createValue(this.oxfordComma(dealTypes)));
    }
    result.push(createLabel('deal'));

    // Display ORIGINATING, NOT_ORIGINATING, or ANYWHERE and location information if applicable
    result.push(createLabel('withCustomer'));
    result.push(createValue(this.translate.instant(`${criteriaPrefix}origin.${criteria.origin}`)));
    if (criteria.origin !== 'ANYWHERE') {
      result.push(createLabel('from'));
      result.push(createValue([criteria.city, criteria.county, criteria.state].filter((one) => !!one).join(', ')));
    }

    // Display the accessory, excusion, F&I, Tax/Fee, or Protection Exclusion depending on criteria type
    // as well as the price or percentage of the item if applicable.
    switch (criteria.type) {
      case CriteraTypes.ACCESSORIES:
        result.push(createLabel('displayAccessory'));
        result.push(createValue(`${criteria.accessory?.title} ($${criteria.accessory?.price})`));
        break;
      case CriteraTypes.EXCLUSIONS:
        result.push(createLabel('exclude'));
        result.push(createValue(criteria.input_name));
        break;
      case CriteraTypes.FI:
        result.push(createLabel('displayFI'));
        result.push(createValue(`${criteria.fiAddOn?.title} ($${criteria.fiAddOn?.price})`));
        break;
      case CriteraTypes.FEES_TAXES:
        result.push(createLabel('addTaxFee'));
        result.push(
          createValue(
            criteria.fee?.amount_type === 'PERCENTAGE_TYPE'
              ? `${criteria.fee?.title} (${criteria.fee?.amount}%)`
              : `${criteria.fee?.title} ($${criteria.fee?.amount})`,
          ),
        );
        break;
      case CriteraTypes.PROTECTION:
        result.push(createLabel('excludeFI'));

        const whereValues = criteria.productExclusions
          ?.map((one) => `${one.product?.title} ($${one.product?.price})`)
          .filter((one) => !!one)
          .sort((a, b) => (a && b && a < b ? -1 : 1));

        if (whereValues) {
          for (const [i, whereValue] of whereValues.entries()) {
            result.push(createValue(whereValue));
            if (i < whereValues.length - 2) {
              result.push({ type: 'label', value: ',' });
            } else if (i === whereValues.length - 2 && i !== 0) {
              result.push({ type: 'label', value: ', and' });
            }
          }
        }
    }

    // Vehicle Information
    result.push(createLabel('for'));
    result.push(createValue(this.translate.instant(`${criteriaPrefix}forVehicle.${criteria.for_vehicle ?? 'all'}`)));

    // Specific Vehicle Information
    if (criteria.for_vehicle === 'specific') {
      result.push(createLabel('where'));
      const onlyMakesSelected = !criteria.vehicleYears?.length && criteria.vehicleMakes?.length;
      let criteriaVehicleModels: CriteriaVehicleModels[] | undefined = criteria.vehicleModels;

      // Remove all duplicate model names if only makes and models were selected
      if (onlyMakesSelected && criteria.vehicleModels) {
        const seen = new Set();
        criteriaVehicleModels = criteria.vehicleModels.filter((elt) => {
          const duplicate = seen.has(elt.model?.name);
          seen.add(elt.model?.name);
          return !duplicate;
        });
      }

      // Create lists of bodyStyles, fuelTypes, and YMM info gramatically (if applicable) to prepare for display
      const bodyStyles = criteria.vehicleBodyStyles
        ? this.oxfordComma(criteria.vehicleBodyStyles.map((one) => one.bodyStyle?.body))
        : undefined;
      const fuelTypes = criteria.fuel_type ? this.oxfordComma(criteria.fuel_type?.split(',')) : undefined;
      const years = this.oxfordComma(criteria.vehicleYears?.map((one) => one.year));
      const makes = this.oxfordComma(
        criteria.vehicleMakes
          ?.map((one) => one.make?.name)
          .filter((one) => !!one)
          .sort((a, b) => (a && b && a < b ? -1 : 1)),
      );
      const models = this.oxfordComma(
        criteriaVehicleModels
          ?.map((one) => one.model?.name)
          .filter((one) => !!one)
          .sort((a, b) => (a && b && a < b ? -1 : 1)),
      );

      // Display either bodyStyle, fuelType, or YMM information
      if (bodyStyles) {
        result.push(createValue(`${this.translate.instant(`${criteriaPrefix}vehicleBodyStyle.in`)} (${bodyStyles})`));
      } else if (fuelTypes) {
        result.push(createValue(`${this.translate.instant(`${criteriaPrefix}vehicleFuelType.in`)} (${fuelTypes})`));
      } else if (years || makes || models) {
        const whereValues = [
          years
            ? `${this.translate.instant(`${criteriaPrefix}vehicleYearPlaceholder.in`)} (${years})`
            : `${this.translate.instant(`${criteriaPrefix}vehicleYearPlaceholder.all`)}`,
          makes
            ? `${this.translate.instant(`${criteriaPrefix}vehicleMakePlaceholder.in`)} (${makes})`
            : this.translate.instant(`${criteriaPrefix}vehicleMakePlaceholder.all`),
          models
            ? `${this.translate.instant(`${criteriaPrefix}vehicleModelPlaceholder.in`)} (${models})`
            : this.translate.instant(`${criteriaPrefix}vehicleModelPlaceholder.all`),
        ];

        for (const [i, whereValue] of whereValues.entries()) {
          result.push(createValue(whereValue));
          if (i < whereValues.length - 2) {
            result.push({ type: 'label', value: ',' });
          } else if (i === whereValues.length - 2 && i !== 0) {
            result.push({ type: 'label', value: ', and' });
          }
        }
      }
    }

    return result;
  }

  // Turn an array into a gramatically correct string of OR items
  oxfordComma(arr?: any[]) {
    if (arr) {
      return arr.length < 3
        ? arr.join(` ${this.translate.instant('addCriteria.labels.or')} `)
        : `${arr.slice(0, -1).join(', ')}, ${this.translate.instant('addCriteria.labels.or')} ${arr[arr.length - 1]}`;
    }
    return undefined;
  }
}
